import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  DialogActions,
  DialogTitle,
  InputBase,
  Stack,
  Table,
  Typography,
  Divider,
  TextField,
  Grid,
} from "@mui/material";
import Intro from "../../atoms/Intro/Intro.component";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import { authHeader } from "../../../helpers";
import { apiConstants } from "../../../constants";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import Modal from "@mui/material/Modal";
import Dialog from "@mui/material/Dialog";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { convertToPlaces } from "../../../helpers/helpers";

const useStyles = makeStyles({
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "5px 0 5px",
  },
  salesOrderFilterTitle: {
    color: "rgba(0, 0, 0, 0.87) !important",
    fontSize: "14 !important",
  },
  salesOrderFilterContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  salesOrdersFilterBar: {
    margin: "14px 0px 16px",
    backgroundColor: "#EFEFEF",
    padding: "16px",
  },
});

const StyledHeader = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.neutral.light,
  textAlign: "left",
  height: "93px",
  paddingLeft: "10px",
  paddingTop: "10px",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.neutral.light,
  padding: "20px",
}));

const StyledTableCell = styled(TableCell)({
  width: "200px",
  fontWeight: 700,
});

const StyledTypography = styled(Typography)({
  fontWeight: "400",
  fontSize: "14px",
});

const columns = [
  { field: "id", headerName: "Production ID", resizable: false },
  {
    field: "created_at",
    headerName: "Date",
    width: 150,
    renderCell: (params) => moment(params.row.created_at).format("DD/MM/YYYY"),
  },
  {
    field: "ItemDescription",
    headerName: "Item Description",
    sortable: true,
    width: 300,
    minWidth: 100,
    valueGetter: (params) => `${params.row.ItemDescription || ""}`,
  },
  {
    field: "organization_name",
    headerName: "Organization Name",
    sortable: true,
    width: 200,
    minWidth: 100,
    valueGetter: (params) => `${params.row.organization_name || ""}`,
  },
  {
    field: "qty",
    headerName: "Quantity",
    sortable: true,
  },
  {
    field: "unit_price",
    headerName: "Unit Price",
    sortable: true,
    renderCell: (params) => convertToPlaces(params.row.original_unit_price),
  },
  {
    field: "grand_total",
    headerName: "Grand Total",
    sortable: true,
    renderCell: (params) => convertToPlaces(params.row.original_grand_total),
  },
];

const GenerateResellerInvoices = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { proceededProductionOrder } = useParams();

  const [organizationSearch, setOrganizationSearch] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [response, setResponse] = useState({ per_page: 10, total: 0 });
  const [loadingComponent, setLoadingComponent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [tot, setTot] = useState();
  const [selectedRow, setSelectedRow] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [openDialogErr, setOpenDialogErr] = useState(false);
  const [dialogMessageErr, setDialogMessageErr] = useState("");
  const [totalExtra, setTotalExtra] = useState();
  const [totDiscount, setTotDiscount] = useState();
  const [totDelivery, setTotDelivery] = useState();
  const [totGST, setTotGST] = useState();
  const [grndTotal, setGrndTotal] = useState();

  // NEW: optional inputs
  const [minQtySurcharge, setMinQtySurcharge] = useState("");
  const [barcodeFees, setBarcodeFees] = useState("");
  const [shippingAgentFees, setShippingAgentFees] = useState("");

  let gst = 0;
  let totalPrice = 0;
  let originalTotal = 0;
  let extraCharges = 0;
  let totalDelivery = 0;
  let grandTotal = 0;
  let totalDiscount = 0;

  const breadcrumbs = [
    <Typography key="3">Generate Reseller Invoices</Typography>,
  ];

  // Reusable fetcher with search
  const fetchSalesOrderItems = (page = 1, search = "") => {
    setLoadingComponent(true);
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };
    let url = `${apiConstants.API_URL}/sales-order-items?have_reseller_invoice=0&page=${page}`;
    if (search) {
      url += `&organization_name=${encodeURIComponent(search)}`;
    }
    fetch(url, requestOptions)
      .then((response) => response.text())
      .then((text) => {
        const data = text && JSON.parse(text);
        setFilteredResults(data?.data);
        setResponse(data?.meta);
        setLoadingComponent(false);
      });
  };

  // Search handler
  const handleOrganizationSearch = (searchValue) => {
    setOrganizationSearch(searchValue);
    setCurrentPage(1);
    fetchSalesOrderItems(1, searchValue);
  };

  // Initial load
  useEffect(() => {
    fetchSalesOrderItems(1, "");
  }, []);

  // Table selection calculation
  useEffect(() => {
    const selectedRowsData = selectedIds.map((id) =>
      filteredResults.find((row) => row.id === id)
    );
    setSelectedRow(selectedRowsData);

    selectedRowsData.map((item) => {
      totalPrice = totalPrice + item?.unit_price * item?.qty;
      originalTotal = originalTotal + item?.original_total_price;
      gst = gst + (item?.gst_amount || 0);
      totalDelivery = totalDelivery + (item?.estimated_shipping_charges || 0);
      extraCharges = extraCharges + (item?.extra_charges || 0);
      return "";
    });
    setTot(totalPrice);
    setTotalExtra(extraCharges);
    setTotDelivery(totalDelivery);
    setTotGST(gst);

    totalDiscount = originalTotal - totalPrice || 0;
    setTotDiscount(totalDiscount);
    grandTotal =
      gst + totalPrice + extraCharges + totalDelivery - totalDiscount;
    setGrndTotal(grandTotal);
  }, [selectedIds, filteredResults]);

  // Pagination handler
  function handlePageChange(page) {
    setCurrentPage(page + 1); // DataGrid is 0-indexed, API is 1-indexed
    fetchSalesOrderItems(page + 1, organizationSearch);
  }

  const handleClick = () => setOpen(true);

  const handelSubmit = () => {
    setOpen(false);
    let reqData = {
      sales_order_item_ids: selectedIds,
      organization_id: 12105,
      comment: "Sample Comment",
    };

    // Append optional fees if provided
    if (minQtySurcharge !== "") {
      reqData.minimum_quantity_surcharge = Number(minQtySurcharge);
    }
    if (barcodeFees !== "") {
      reqData.barcode_fees = Number(barcodeFees);
    }
    if (shippingAgentFees !== "") {
      reqData.shipping_agent_fees = Number(shippingAgentFees);
    }

    const requestOptions = {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify(reqData),
    };

    fetch(`${apiConstants.API_URL}/reseller-invoices`, requestOptions).then(
      (response) => {
        response.text().then((text) => {
          if (response.status === 200) {
            setDialogMessage("Reseller Invoice Created Successfully");
            setOpenDialog(true);
          } else {
            setDialogMessageErr("Reseller Invoice Create Unsuccessful");
            setOpenDialogErr(true);
          }
        });
      }
    );
  };

  const handleClose = () => setOpen(false);
  const handleDialogClose = () => setOpenDialog(false);
  function handleDialogCloseErr() {
    setOpenDialogErr(false);
  }

  return (
    <>
      <Box className={classes.headerContainer}>
        <Intro
          pageTitle={"Generate Reseller Invoices"}
          pageTitleShort=""
          breadcrumbs={breadcrumbs}
          additionalWidgets=""
        />
      </Box>

      {/* FILTER BAR WITH SEARCH */}
      <Box className={classes.salesOrdersFilterBar}>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Button
            disabled={selectedIds.length === 0}
            variant="contained"
            className={classes.filterButton}
            onClick={handleClick}
          >
            Generate Reseller Invoice
          </Button>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <InputBase
              placeholder="Search Organization"
              value={organizationSearch}
              onChange={(e) => setOrganizationSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleOrganizationSearch(e.target.value);
                }
              }}
              sx={{
                ml: 2,
                flex: 1,
                padding: "6px 12px",
                border: "1px solid #cfcfcf",
                borderRadius: "4px",
                background: "#fff",
                minWidth: 200,
              }}
              inputProps={{ "aria-label": "search organizations" }}
            />
            <Button
              variant="outlined"
              onClick={() => handleOrganizationSearch(organizationSearch)}
              sx={{ ml: 1 }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Box>

      {/* TABLE */}
      <Box style={{ width: "100%" }}>
        {loadingComponent ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <CircularProgress size={50} />
          </Box>
        ) : (
          <DataGrid
            autoHeight={true}
            className={classes.dataGrid}
            rows={filteredResults}
            pageSize={response.per_page}
            pagination
            getRowId={(row) => row.id}
            checkboxSelection
            rowCount={response.total}
            onPageChange={handlePageChange}
            columns={columns}
            paginationMode="server"
            onSelectionModelChange={(ids) => setSelectedIds(ids)}
          />
        )}
      </Box>

      {/* INVOICE DIALOG */}
      <Dialog fullScreen open={open} onClose={handleClose}>
        <>
          <AppBar elevation={0} sx={{ position: "relative" }}>
            <Toolbar sx={{ px: { sm: 2 } }}>
              <Typography sx={{ flex: 1 }} variant="h6" component="div">
                {/* Dialog Title */}
              </Typography>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              padding: "40px 25px 40px 40px;",
            }}
            style={{ justifyContent: "center" }}
          >
            <div>
              <Card style={{ minWidth: "500px", margin: "20px" }}>
                <Stack direction={"column"} justifyContent="center">
                  <StyledHeader>
                    <Typography fontSize={"24px"}>Items</Typography>
                  </StyledHeader>
                  <Box>
                    <Table>
                      <TableHead>
                        <TableRow style={{ height: "56px" }}>
                          <StyledTableCell>Description</StyledTableCell>
                          <StyledTableCell align="right">
                            Unit Price
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            Quantity
                          </StyledTableCell>
                          <StyledTableCell align="right">Price</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRow?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {item?.ItemDescription ? item?.ItemDescription : ""}
                            </TableCell>
                            <TableCell align="right">
                              {convertToPlaces(item?.original_unit_price)}
                            </TableCell>
                            <TableCell align="right">{item?.qty}</TableCell>
                            <TableCell align="right">
                              {convertToPlaces(
                                item?.original_unit_price * item?.qty
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* NEW: Optional additional charges inputs */}
                  <Divider sx={{ mt: 2 }} />
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ mb: 1 }} fontWeight={600}>
                      Additional Charges (Optional)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Minimum Quantity Surcharges"
                          type="number"
                          value={minQtySurcharge}
                          onChange={(e) => setMinQtySurcharge(e.target.value)}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Barcode Fees"
                          type="number"
                          value={barcodeFees}
                          onChange={(e) => setBarcodeFees(e.target.value)}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Shipping Agent Fees"
                          type="number"
                          value={shippingAgentFees}
                          onChange={(e) => setShippingAgentFees(e.target.value)}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                      Leave blank if not applicable.
                    </Typography>
                  </Box>
                </Stack>
              </Card>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Button variant="contained" color="error" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handelSubmit}>
                  generate Invoice
                </Button>
              </Stack>
            </div>
          </Stack>
        </>
      </Dialog>

      {/* SUCCESS DIALOG */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <CheckCircleIcon
            color="success"
            sx={{
              position: "relative",
              top: "0.25rem",
              mr: 1,
              display: { xs: "block", sm: "inline-block" },
              margin: { xs: "0 0 1rem 0", sm: "0 0.6rem 0 0" },
            }}
          />
          {dialogMessage}
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              handleDialogClose();
              navigate("/admin/reseller-invoices");
            }}
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ERROR DIALOG */}
      <Dialog
        open={openDialogErr}
        onClose={handleDialogCloseErr}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <CheckCircleIcon
            color="error"
            sx={{
              position: "relative",
              top: "0.25rem",
              mr: 1,
              display: { xs: "block", sm: "inline-block" },
              margin: { xs: "0 0 1rem 0", sm: "0 0.6rem 0 0" },
            }}
          />
          {dialogMessageErr}
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleDialogCloseErr} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateResellerInvoices;
